import React, { useState } from 'react';
import { Person } from '../App';
import './PersonManager.css';

interface PersonManagerProps {
  persons: Person[];
  onAddPerson: (name: string) => Promise<any>;
  onDeletePerson: (personId: number) => Promise<any>;
}

const PersonManager: React.FC<PersonManagerProps> = ({ persons, onAddPerson, onDeletePerson }) => {
  const [newPersonName, setNewPersonName] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [deletingPerson, setDeletingPerson] = useState<number | null>(null);

  const handleAddPerson = async () => {
    if (!newPersonName.trim()) {
      setMessage('❌ Bitte einen Namen eingeben!');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      await onAddPerson(newPersonName);
      setMessage('✅ Person erfolgreich hinzugefügt!');
      setNewPersonName('');
    } catch (error: any) {
      setMessage(`❌ ${error.message || 'Fehler beim Hinzufügen!'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePerson = async (personId: number) => {
    setLoading(true);
    setMessage('');

    try {
      await onDeletePerson(personId);
      setMessage('✅ Person erfolgreich gelöscht!');
      setDeletingPerson(null);
    } catch (error) {
      setMessage('❌ Fehler beim Löschen!');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (personId: number) => {
    setDeletingPerson(personId);
    setMessage('');
  };

  const cancelDelete = () => {
    setDeletingPerson(null);
    setMessage('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleAddPerson();
    }
  };

  return (
    <div className="person-manager">
      <h2>👥 Personen-Verwaltung</h2>

      <div className="add-person-section">
        <h3>Neue Person hinzufügen</h3>
        
        {message && (
          <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
            {message}
          </div>
        )}

        <div className="form-group">
          <label>Name:</label>
          <input
            type="text"
            value={newPersonName}
            onChange={(e) => setNewPersonName(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="z.B. Max Mustermann"
            maxLength={50}
            disabled={loading}
          />
        </div>

        <button
          className="add-button"
          onClick={handleAddPerson}
          disabled={loading || !newPersonName.trim()}
        >
          {loading ? 'Füge hinzu...' : 'Person hinzufügen'}
        </button>
      </div>

      <div className="existing-persons">
        <h3>Vorhandene Personen ({persons.length})</h3>
        {persons.length === 0 ? (
          <div className="no-persons">
            <p>Noch keine Personen vorhanden.</p>
            <p>Füge die erste Person hinzu, um mit der Rechnungsverfolgung zu beginnen!</p>
          </div>
        ) : (
          <div className="persons-table">
            <div className="table-header">
              <span>Name</span>
              <span>Käufe</span>
              <span>Ausgegeben</span>
              <span>Gespart/Verloren</span>
              <span>Seit</span>
              <span>Aktionen</span>
            </div>
            {persons.map(person => (
              <div key={person.id} className="table-row">
                <span className="person-name">{person.name}</span>
                <span>{person.purchaseCount}</span>
                <span className="money-spent">€{person.totalSpent.toFixed(2)}</span>
                <span className={`savings ${person.totalSavings >= 0 ? 'positive' : 'negative'}`}>
                  €{person.totalSavings.toFixed(2)}
                </span>
                <span className="created-date">
                  {new Date(person.createdAt).toLocaleDateString('de-DE')}
                </span>
                <span className="actions">
                  {deletingPerson === person.id ? (
                    <div className="delete-actions">
                      <span className="delete-confirmation">Wirklich löschen?</span>
                      <button
                        className="confirm-delete-button"
                        onClick={() => handleDeletePerson(person.id)}
                        disabled={loading}
                      >
                        ✓
                      </button>
                      <button
                        className="cancel-delete-button"
                        onClick={cancelDelete}
                        disabled={loading}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <button
                      className="delete-button"
                      onClick={() => confirmDelete(person.id)}
                      disabled={loading}
                      title="Person löschen"
                    >
                      🗑️
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonManager;